import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock3, Headphones, Download } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { agreementDownloadUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1592805144716-feeccccef5ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBkcml2aW5nJTIwaGlnaHdheSUyMHN1bnNldCUyMGFtZXJpY2F8ZW58MHx8fHwxNzkwMzQ3ODY5fDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const Lines = [
  [{ t: "Insurance that" }],
  [{ t: "keeps America's" }],
  [{ t: "trucks", accent: true }, { t: " moving." }],
];

const CHIPS = [
  { icon: Clock3, label: "FMCSA instant filings" },
  { icon: ShieldCheck, label: "A+ rated carrier network" },
  { icon: Headphones, label: "24/7 live claims" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const stickerY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white pt-28 pb-16 sm:pt-32 lg:min-h-screen lg:items-center lg:pt-24" data-testid="hero-section">
      <div className="bg-grid pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black_20%,transparent_75%)]" />
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-6 xl:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
            className="eyebrow"
          >
            Commercial truck &amp; fleet insurance — all 50 states
          </motion.p>

          <h1 className="mt-5 font-display text-5xl font-bold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl xl:text-[5.2rem]">
            {Lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.18 + i * 0.13, ease: EASE }}
                >
                  {line.map((part, j) => (
                    <span key={j} className={part.accent ? "text-brand-blue" : undefined}>
                      {part.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
          >
            Fleetguard protects owner-operators and fleets across the USA — with filings in
            minutes, live claims around the clock, and{" "}
            <span className="font-semibold text-brand-navy">up to 50% off your first premium</span>{" "}
            when you switch.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("quote")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-base font-semibold text-white shadow-xl shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-bluedeep"
            >
              Get a Quote
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </button>
            <a
              href={agreementDownloadUrl}
              data-testid="hero-cta-download"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-base font-semibold text-brand-navy transition-all hover:border-brand-blue hover:text-brand-blue"
            >
              <Download className="h-4 w-4" /> Download Agreement
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {CHIPS.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                <c.icon className="h-4 w-4 text-brand-blue" />
                {c.label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-6 xl:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <div className="absolute -bottom-5 -right-5 h-full w-full rounded-3xl border-2 border-brand-blue/15" />
            <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/20">
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 1.2, delay: 0.45, ease: EASE }}
                className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]"
              >
                <motion.img
                  src={HERO_IMG}
                  alt="Semi truck driving the American highway at sunset"
                  style={{ y: imgY }}
                  className="absolute inset-0 h-full w-full scale-[1.18] object-cover"
                  data-testid="hero-truck-image"
                />
              </motion.div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-navy/35 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div className="rounded-2xl bg-white/90 px-4 py-3 backdrop-blur">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Coverage bound today</p>
                  <p className="font-display text-lg font-bold text-brand-navy">Certificates in minutes</p>
                </div>
              </div>
            </div>

            <motion.div
              style={{ y: stickerY }}
              initial={{ scale: 0, rotate: -24 }}
              animate={{ scale: 1, rotate: -6 }}
              transition={{ type: "spring", stiffness: 200, damping: 15, delay: 1.25 }}
              className="absolute -left-3 top-8 sm:-left-8"
              data-testid="hero-discount-badge"
            >
              <div className="rounded-2xl bg-brand-orange px-5 py-4 text-white shadow-2xl shadow-orange-600/40">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em]">Up to</p>
                <p className="font-display text-4xl font-extrabold leading-none">50%</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider">off first premium</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
